// ============================================================
// USE BALLOT BOXES
//
// Fetches the list of ballot boxes from Supabase and exposes an
// addBox(name) function for admins. Voters store ballot box
// choices as the box NAME (text), not id, so consumers of this
// hook only need the names.
//
// Caching: the hook fetches once on mount per instance. Multiple
// profiles opened in sequence each re-fetch, which is ~40KB at
// 588 rows — acceptable. If this grows, lift state up to App or
// add a session cache.
// ============================================================

import { useCallback, useEffect, useState } from 'react';

export function useBallotBoxes(supabase) {
  const [boxes, setBoxes] = useState([]);      // string[]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch all ballot boxes, paginating past Supabase's 1000-row default.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const PAGE = 1000;
      let all = [];
      let from = 0;
      while (true) {
        const { data, error: err } = await supabase
          .from('ballot_boxes')
          .select('name')
          .order('name', { ascending: true })
          .range(from, from + PAGE - 1);
        if (err) throw err;
        if (!data || data.length === 0) break;
        all = all.concat(data.map(r => r.name));
        if (data.length < PAGE) break;
        from += PAGE;
      }
      setBoxes(all);
    } catch (e) {
      console.error('[ballot_boxes] fetch failed:', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [supabase]);
  
  useEffect(() => { load(); }, [load]);
  
  // Insert a new box and return its name (trimmed) on success.
  // If a box with that name already exists, the DB's UNIQUE
  // constraint returns an error with code '23505'; we treat that
  // as a silent success (refetch + return the existing name).
  const addBox = useCallback(async (rawName) => {
    const name = (rawName || '').trim();
    if (!name) return null;
    
    const { error: insErr } = await supabase
      .from('ballot_boxes')
      .insert({ name });
    
    if (insErr) {
      // 23505 = unique_violation
      if (insErr.code !== '23505') {
        console.error('[ballot_boxes] insert failed:', insErr);
        throw insErr;
      }
    }
    
    // Refetch so UI sees the new entry (or confirms the existing one).
    await load();
    return name;
  }, [supabase, load]);
  
  return { boxes, loading, error, addBox, reload: load };
}
